/**
 * Chat Widget Component
 *
 * Floating chat interface with expandable message window.
 * Displays messages, handles user input, and shows loading states.
 */

import React, { useState, useRef, useEffect } from 'react';
import { useChat } from '../../contexts/ChatContext';
import { MessageCircle, X, Send } from 'lucide-react';
import ReactMarkdown from 'react-markdown';
import '../../styles/chat.css';

interface ChatMessage {
  id: string;
  role: 'user' | 'assistant';
  content: string;
  timestamp: Date;
}

export const ChatWidget: React.FC = () => {
  const { isOpen, messages, loading, openChat, closeChat, sendMessage } = useChat();
  const [inputValue, setInputValue] = useState('');
  const messagesEndRef = useRef<HTMLDivElement>(null);

  // Auto-scroll to bottom when new messages arrive
  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [messages]);

  const handleSend = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!inputValue.trim() || loading) return;

    await sendMessage(inputValue);
    setInputValue('');
  };

  if (!isOpen) {
    return (
      <button
        onClick={openChat}
        className="su-chat-button"
        aria-label="Open chat"
      >
        <MessageCircle size={24} />
      </button>
    );
  }

  return (
    <div className="su-chat-widget">
      {/* Header */}
      <div className="su-chat-header">
        <div className="su-chat-header-content">
          <MessageCircle size={20} />
          <h3>Support Chat</h3>
        </div>
        <button onClick={closeChat} className="su-chat-close" aria-label="Close chat">
          <X size={20} />
        </button>
      </div>

      {/* Messages */}
      <div className="su-chat-messages">
        {messages.map((msg: ChatMessage) => (
          <div
            key={msg.id}
            className={`su-chat-message ${msg.role === 'user' ? 'su-chat-message-user' : 'su-chat-message-assistant'}`}
          >
            <div className="su-chat-message-content">
              {msg.role === 'assistant' ? (
                <ReactMarkdown
                  components={{
                    a: ({ href, children }) => {
                      const isComposioLink = href?.includes('connect.composio.dev') || href?.includes('backend.composio.dev');
                      if (isComposioLink && href) {
                        return (
                          <button
                            onClick={() => {
                              const w = 500, h = 700;
                              const left = window.screenX + (window.outerWidth - w) / 2;
                              const top = window.screenY + (window.outerHeight - h) / 2;
                              window.open(href, 'composio_connect', `width=${w},height=${h},left=${left},top=${top},toolbar=no,menubar=no,scrollbars=yes`);
                            }}
                            style={{
                              display: 'block',
                              margin: '12px 0',
                              padding: '10px 16px',
                              background: '#7C3AED',
                              color: '#fff',
                              border: 'none',
                              borderRadius: '8px',
                              cursor: 'pointer',
                              fontWeight: 600,
                              fontSize: '14px',
                              textDecoration: 'none',
                              textAlign: 'center',
                              width: '100%',
                            }}
                          >
                            {children}
                          </button>
                        );
                      }
                      return <a href={href} target="_blank" rel="noopener noreferrer" style={{ textDecoration: 'underline', color: '#AE7AFF' }}>{children}</a>;
                    },
                  }}
                >{msg.content}</ReactMarkdown>
              ) : (
                msg.content
              )}
            </div>
            <div className="su-chat-message-timestamp">
              {new Date(msg.timestamp).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}
            </div>
          </div>
        ))}

        {loading && (
          <div className="su-chat-message su-chat-message-assistant">
            <div className="su-chat-typing-indicator">
              <span></span>
              <span></span>
              <span></span>
            </div>
          </div>
        )}

        <div ref={messagesEndRef} />
      </div>

      {/* Input */}
      <form onSubmit={handleSend} className="su-chat-input-form">
        <input
          type="text"
          value={inputValue}
          onChange={(e) => setInputValue(e.target.value)}
          placeholder="Type your message..."
          className="su-chat-input"
          disabled={loading}
          maxLength={1000}
        />
        <button
          type="submit"
          className="su-chat-send-button"
          disabled={loading || !inputValue.trim()}
          aria-label="Send message"
        >
          <Send size={20} />
        </button>
      </form>
    </div>
  );
};
